import { seasonOf, seasonLabel } from '../utils/season';
import React from 'react';

/* ====================================================================
   CLUB LEADERBOARD TABLE — sortable all-time standings with win rate
   ==================================================================== */
function ClubLeaderboardTable({ dashLeaderboard, openProfile, openTeamProfile, comps, compYearFilter, seasonStartMonth = 1, setCompYearFilter }) {
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState('desc');
  const [typeFilter, setTypeFilter] = React.useState('all');

  const toggleSort = (key) => {
    if (sortKey === key) {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortKey(null); setSortDir('desc'); }
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <i className="fa fa-sort" style={{ opacity: 0.3, marginLeft: 4 }}></i>;
    return <i className={`fa fa-sort-${sortDir === 'desc' ? 'down' : 'up'}`} style={{ marginLeft: 4 }}></i>;
  };

  const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const standings = React.useMemo(() => {
    if (!dashLeaderboard || !dashLeaderboard.standings) return [];
    const filtered = dashLeaderboard.standings.filter(s => typeFilter === 'all' || s.type === typeFilter);
    const rows = filtered.map((s, i) => ({
      ...s,
      _origIdx: i,
      winRate: (s.played - (s.byes || 0)) > 0 ? (s.wins / (s.played - (s.byes || 0))) * 100 : 0,
      diff: (s.pointsFor || 0) - (s.pointsAgainst || 0),
      rating: s.rating ?? 0,
      endsWon: s.endsWon || 0,
      endsLost: s.endsLost || 0,
      compWins: s.compWins || 0,
    }));
    if (!sortKey) return rows;
    const sorted = [...rows].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'name') {
        av = (av || '').toLowerCase(); bv = (bv || '').toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'desc' ? (bv - av) : (av - bv);
    });
    return sorted;
  }, [dashLeaderboard, sortKey, sortDir, typeFilter]);

  const years = React.useMemo(() => {
    if (!comps) return [];
    return [...new Set(comps.map(c => seasonOf(c.startDate, seasonStartMonth)).filter(y => y !== null))].sort().reverse();
  }, [comps, seasonStartMonth]);

  return (
    <div className="w3-card w3-round w3-white">
      <div className="w3-container w3-padding">
        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '0' }}>
            {[['all', 'All'], ['player', 'Players'], ['team', 'Teams']].map(([val, label]) => (
              <button key={val}
                className={`w3-button w3-small ${typeFilter === val ? 'w3-theme' : 'w3-theme-l4'}`}
                style={{ borderRight: val !== 'team' ? '1px solid rgba(0,0,0,0.1)' : undefined, borderRadius: val === 'all' ? '4px 0 0 4px' : val === 'team' ? '0 4px 4px 0' : '0' }}
                onClick={() => setTypeFilter(val)}>
                {val === 'player' && <i className="fa fa-user w3-margin-right"></i>}
                {val === 'team' && <i className="fa fa-shield w3-margin-right"></i>}
                {val === 'all' && <i className="fa fa-users w3-margin-right"></i>}
                {label}
              </button>
            ))}
          </div>
          {years.length > 1 && setCompYearFilter && (
            <select
              className="w3-select w3-border w3-round w3-small"
              style={{ width: 'auto', maxWidth: '130px' }}
              value={compYearFilter || 'all'}
              onChange={e => setCompYearFilter(e.target.value)}>
              <option value="all">All Seasons</option>
              {years.map(y => <option key={y} value={y}>{seasonLabel(y, seasonStartMonth)}</option>)}
            </select>
          )}
          <span className="w3-small w3-text-grey">{standings.length} {typeFilter === 'player' ? 'players' : typeFilter === 'team' ? 'teams' : 'entries'}</span>
        </div>
        {standings.length === 0 ? (
          <p className="w3-text-grey w3-center" style={{ padding: '16px 0' }}>
            <em>No results yet.</em>
          </p>
        ) : (<>
          <table className="w3-table w3-bordered w3-striped">
            <thead>
              <tr className="w3-theme-l4">
                <th style={{ width: '36px' }}>#</th>
                <th style={{ ...thStyle }} onClick={() => toggleSort('name')}>Name{sortIcon('name')}</th>
                <th style={{ ...thStyle, width: '50px' }} onClick={() => toggleSort('rating')}>Rating{sortIcon('rating')}</th>
                <th style={{ width: '32px' }}>Type</th>
                <th style={{ ...thStyle, width: '40px' }} onClick={() => toggleSort('played')}>P{sortIcon('played')}</th>
                <th style={{ ...thStyle, width: '40px' }} onClick={() => toggleSort('wins')}>W{sortIcon('wins')}</th>
                <th style={{ ...thStyle, width: '40px' }} onClick={() => toggleSort('losses')}>L{sortIcon('losses')}</th>
                <th style={{ ...thStyle, width: '40px' }} onClick={() => toggleSort('draws')}>D{sortIcon('draws')}</th>
                <th style={{ ...thStyle, width: '50px' }} onClick={() => toggleSort('winRate')}>Win%{sortIcon('winRate')}</th>
                <th style={{ ...thStyle, width: '50px' }} onClick={() => toggleSort('endsWon')}>Ends W{sortIcon('endsWon')}</th>
                <th style={{ ...thStyle, width: '50px' }} onClick={() => toggleSort('endsLost')}>Ends L{sortIcon('endsLost')}</th>
                <th style={{ ...thStyle, width: '50px' }} onClick={() => toggleSort('pointsFor')}>Pts For{sortIcon('pointsFor')}</th>
                <th style={{ ...thStyle, width: '50px' }} onClick={() => toggleSort('pointsAgainst')}>Pts Agst{sortIcon('pointsAgainst')}</th>
                <th style={{ ...thStyle, width: '50px' }} onClick={() => toggleSort('diff')}>Diff{sortIcon('diff')}</th>
                <th style={{ ...thStyle, width: '50px' }} onClick={() => toggleSort('compWins')}>Comp W{sortIcon('compWins')}</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((s, i) => (
                <tr key={`${s.type}-${s.id}`}
                  className="w3-hover-light-grey" style={{ cursor: 'pointer' }}
                  onClick={() => { if (s.type === 'player') openProfile(s.id); else openTeamProfile(s.id); }}>
                  <td style={{ fontWeight: 700 }}>{sortKey ? i + 1 : s._origIdx + 1}</td>
                  <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>
                    <i className={`fa ${s.type === 'team' ? 'fa-shield' : 'fa-user'} w3-margin-right w3-small`}
                      style={{ color: s.type === 'team' ? '#607d8b' : '#999', width: '14px' }}></i>
                    {s.name}
                  </td>
                  <td style={{ fontWeight: 700 }}>{s.rating}</td>
                  <td><span className={`w3-tag w3-round w3-small ${s.type === 'team' ? 'w3-theme-l3' : 'w3-light-grey'}`} style={{ fontSize: 'var(--gs-text-xs)', padding: '1px 6px' }}>{s.type === 'team' ? 'Team' : 'Player'}</span></td>
                  <td>{s.played}</td>
                  <td className="w3-text-green">{s.wins}</td>
                  <td className="w3-text-red">{s.losses}</td>
                  <td>{s.draws}</td>
                  <td>{s.winRate.toFixed(0)}%</td>
                  <td>{s.endsWon}</td>
                  <td>{s.endsLost}</td>
                  <td>{s.pointsFor}</td>
                  <td>{s.pointsAgainst}</td>
                  <td className={s.diff >= 0 ? 'w3-text-green' : 'w3-text-red'}>
                    {s.diff > 0 ? '+' : ''}{s.diff}
                  </td>
                  <td>{s.compWins}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortKey && (
            <p className="w3-tiny w3-text-blue" style={{ marginTop: '4px', cursor: 'pointer' }} onClick={() => { setSortKey(null); setSortDir('desc'); }}>
              <i className="fa fa-undo w3-margin-right"></i>Reset to default order
            </p>
          )}
        </>)}
      </div>
    </div>
  );
}

export default ClubLeaderboardTable;
